import { useState, useCallback, useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Where } from '../../../../../baseComponents/TwinTable/types'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import withLoading from '../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../utils/reducers/company/employees'
import { WorkSheetGroupStatusesPayload } from '../../../../../utils/reducers/sells/workSheetGroupStatus'
import { WorkSheetAllProductStatusFlowSubHeader, WorkSheetProductStatusFlowSubHeader } from './WorkSheetProductStatusFlowSubHeaders'
import WorkSheetProductStatusFlowInner from './WorkSheetProductStatusFlowInner'
import { WorkSheetProductStatusURLs, StatusFlowData } from './WorkSheetProductStatusFlowInner/types'

export interface WorkSheetProductStatusFlowBaseProps {
    readOnly?: boolean
}

export interface WorkSheetProductStatusFlowBaseLogicProps {
    workSheetProductStatusURLs: WorkSheetProductStatusURLs
    workSheetProductStatuses: WorkSheetGroupStatusesPayload
}

type WorkSheetsProductStatusFlowProps = WorkSheetProductStatusFlowBaseProps & WorkSheetsProductStatusFlowLogicProps & {}

const WorkSheetsProductStatusFlow: React.FC<WorkSheetsProductStatusFlowProps> = ({ ...logic }) => {
    const { data, setData, getData, handleSelectedData } = useWorkSheetsProductStatusFlowLogic({ ...logic })
    return (<div className='flex flex-auto h-1 worksheet_product_flow flex-col'>
        <WorkSheetAllProductStatusFlowSubHeader data={data} handleSelectedData={handleSelectedData} WorkSheetGroupStatus={logic.workSheetProductStatuses} />
        <WorkSheetProductStatusFlowInner {...{ ...logic, data, setData, getData, showAllWorkSheets: true }} />
    </div>)
}
interface WorkSheetsProductStatusFlowLogicProps extends WorkSheetProductStatusFlowBaseLogicProps {}

const useWorkSheetsProductStatusFlowLogic = ({ workSheetProductStatusURLs, workSheetProductStatuses }: WorkSheetsProductStatusFlowLogicProps) => {
    const [data, setData] = useState<StatusFlowData>({
        searchValue: '',
        selectedEmployeeId: null,
        selectedProduct: null,
        orderAscDesc: 'ASC',
        products: null,
        WorkSheetGroupStatusId: workSheetProductStatuses?.WorkSheetActiveStatusGroups?.[0]?.id || 0
    })

    const getData = useCallback(async () => {
        let where: Where = {}
        if (data.searchValue && data.searchValue.length) {
            where['$or'] = {
                'CopyProduct.name': { $iLike: '%' + data.searchValue + '%' },
                'Product.reference': { $iLike: '%' + data.searchValue + '%' },
            }
        }
        if (data.selectedEmployeeId) {
            where['AssignedEmployeeId'] = data.selectedEmployeeId
        }

        where['WorkSheet.WorkSheetGroupStatusId'] = data.WorkSheetGroupStatusId
        const result = await twinFetchPostJSON(workSheetProductStatusURLs.getProducts || '', {
            'ProductId': data.selectedProduct?.id || null,
            where,
            order: [['WorkSheetId', data.orderAscDesc], ['nLine', 'ASC']],
            limit: 1000
        })
        if (result) {
            const dictProductWithWorkSheetStatus: TwinDictionary = {}
            for (const element of result || []) {
                const StatusId = element.WorkSheetProductStatusId
                if (!dictProductWithWorkSheetStatus[StatusId]) {
                    dictProductWithWorkSheetStatus[StatusId] = []
                }
                dictProductWithWorkSheetStatus[StatusId].push({
                    ...element,
                    originalContainer: StatusId
                })
            }
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    products: dictProductWithWorkSheetStatus
                }
            })
        } else {
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    products: {}
                }
            })
        }
    }, [data.orderAscDesc, data.searchValue, data.selectedEmployeeId, data.selectedProduct?.id, workSheetProductStatusURLs.getProducts, data.WorkSheetGroupStatusId])

    const handleSelectedData = useCallback((field: keyof StatusFlowData, value: any) => {
        setData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return {
                ...copy,
                [field]: value
            }
        })
    }, [setData])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, setData, getData, handleSelectedData }
}
type SingleWorkSheetProductStatusFlowProps = WorkSheetProductStatusFlowBaseProps & SingleWorkSheetProductStatusFlowLogicProps & {
}

export const SingleWorkSheetProductStatusFlow: React.FC<SingleWorkSheetProductStatusFlowProps> = ({ ...logic }) => {
    const { data, setData, getData, handleSelectedData } = useSingleWorkSheetProductStatusFlowLogic({ ...logic })
    return (<div className='flex flex-auto h-1 worksheet_product_flow flex-col'>
        <WorkSheetProductStatusFlowSubHeader data={data} handleSelectedData={handleSelectedData}/>
        <WorkSheetProductStatusFlowInner {...{ ...logic, data, setData, getData }} />
    </div>)
}

interface SingleWorkSheetProductStatusFlowLogicProps extends WorkSheetProductStatusFlowBaseLogicProps {
    WorkSheetGroupStatusId: number
    WorkSheetId: number
}

const useSingleWorkSheetProductStatusFlowLogic = ({ workSheetProductStatusURLs, WorkSheetId, WorkSheetGroupStatusId }: SingleWorkSheetProductStatusFlowLogicProps) => {
    const [data, setData] = useState<StatusFlowData>({
        searchValue: '',
        selectedEmployeeId: null,
        selectedProduct: null,
        products: null,
        WorkSheetGroupStatusId
    })

    const getData = useCallback(async () => {
        let where: Where = {}
        if (data.searchValue && data.searchValue.length) {
            where['$or'] = {
                'CopyProduct.name': { $iLike: '%' + data.searchValue + '%' },
                'Product.reference': { $iLike: '%' + data.searchValue + '%' },
            }
        }
        if (data.selectedEmployeeId) {
            where['AssignedEmployeeId'] = data.selectedEmployeeId
        }

        where['WorkSheetId'] = WorkSheetId
        const result = await twinFetchPostJSON(workSheetProductStatusURLs.getProducts || '', {
            'ProductId': data.selectedProduct?.id || null,
            where,
            order: [['nLine', 'ASC']]
        })
        if (result) {
            const dictProductWithWorkSheetStatus: TwinDictionary = {}
            for (const element of result || []) {
                const StatusId = element.WorkSheetProductStatusId
                if (!dictProductWithWorkSheetStatus[StatusId]) {
                    dictProductWithWorkSheetStatus[StatusId] = []
                }
                dictProductWithWorkSheetStatus[StatusId].push({
                    ...element,
                    originalContainer: StatusId
                })
            }
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    products: dictProductWithWorkSheetStatus
                }
            })
        } else {
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    products: {}
                }
            })
        }
    }, [data.searchValue, data.selectedEmployeeId, data.selectedProduct?.id, workSheetProductStatusURLs.getProducts, WorkSheetId])

    const handleSelectedData = useCallback((field: keyof StatusFlowData, value: any) => {
        setData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return {
                ...copy,
                [field]: value
            }
        })
    }, [setData])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, setData, getData, handleSelectedData }
}

const employeesDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxEmployees = ConnectedProps<typeof employeesConnect>
const mapEmployeesConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const employeesConnect = connect(mapEmployeesConnector, employeesDispatch)
const employeesConnectLoading = withLoading(WorkSheetsProductStatusFlow, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const WorkSheetsProductStatusFlowConnect = employeesConnect(employeesConnectLoading)

export default WorkSheetsProductStatusFlowConnect